import type { RemoteSelection } from '@/hooks/useRealtimeCollaboration';

interface RemoteSelectionIndicatorProps {
  remoteSelections: Map<string, RemoteSelection>;
}

/**
 * Renders selection indicators for elements selected by other users
 * Shows colored outlines around selected elements with user identification
 */
export function RemoteSelectionIndicator({ remoteSelections }: RemoteSelectionIndicatorProps) {
  // Clean up stale selections (older than 30 seconds)
  const now = Date.now();
  const STALE_THRESHOLD_MS = 30000;

  // Generate unique colors for each user based on their userId
  const getUserColor = (userId: string) => {
    // Simple hash function to generate consistent color per user
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = hash % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  return (
    <>
      {Array.from(remoteSelections.values()).map(selection => {
        // Skip stale selections
        if (now - selection.timestamp > STALE_THRESHOLD_MS) {
          return null;
        }

        const color = getUserColor(selection.userId);

        return selection.elementIds.map(elementId => {
          // Find the element in the DOM
          const element = document.querySelector(
            `[data-${selection.elementType}-id="${elementId}"]`,
          ) as HTMLElement;

          if (!element) return null;

          // Get element position and size
          const rect = element.getBoundingClientRect();
          const container = document.querySelector('.floor-plan-viewport');
          if (!container) return null;

          const containerRect = container.getBoundingClientRect();
          const relativeX = rect.left - containerRect.left;
          const relativeY = rect.top - containerRect.top;

          const key = `${selection.userId}:${selection.elementType}:${elementId}`;

          return (
            <div
              key={key}
              className="pointer-events-none absolute z-40 transition-all duration-200"
              style={{
                left: `${relativeX}px`,
                top: `${relativeY}px`,
                width: `${rect.width}px`,
                height: `${rect.height}px`,
              }}
            >
              {/* Selection outline */}
              <div
                className="absolute inset-0 rounded-sm"
                style={{
                  border: `2px solid ${color}`,
                  boxShadow: `0 0 0 1px rgba(255, 255, 255, 0.5), 0 0 8px ${color}`,
                }}
              />

              {/* User name label - only show on first element */}
              {selection.elementIds[0] === elementId && (
                <div
                  className="absolute -top-7 left-0 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-white shadow-lg"
                  style={{ backgroundColor: color }}
                >
                  {selection.userName}
                </div>
              )}
            </div>
          );
        });
      })}
    </>
  );
}
